import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydDatePicker from '../../../../commonDesign/SydDatePicker'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import Markup from '../../../../atoms/Markup'
import { fetchTaskCategories } from './wealthboxQueryHelpers'
import { htmlToPlainText } from './wealthboxHtml'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

// Wealthbox `/v1/tasks` priority is a STRING enum, NOT an integer. The original
// SMT-1352 ticket text said 1=High/2=Normal/3=Low - that's wrong; verified
// against https://dev.wealthbox.com (response body shows e.g. `"priority":
// "Medium"`) and against a real tenant response. Documented values:
//   - Reading: `None` | `Low` | `Medium` | `High` | null
//   - Writing (create/update): `Low` | `Medium` | `High` (None can't be set
//     server-side; clear by sending priority: null)
// Order in the dropdown follows visual severity (High first) for parity with
// the rest of the platform's priority pickers.
const PRIORITY_OPTIONS = [
  { label: '(None)', value: '' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' }
]

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'
const MODE_CONFIRMING_DELETE = 'confirming-delete'

const labelStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  marginBottom: '4px'
}

const valueStyle = {
  fontSize: 'var(--font-size-sm)',
  marginBottom: '12px'
}

function buildFormDefaults (task) {
  return {
    name: task?.name || '',
    due_date: task?.due_date ? dayjs(task.due_date).format('YYYY-MM-DD') : '',
    // Wealthbox stores task descriptions as HTML (auto-wraps plaintext in
    // <div>, preserves rich markup added in Wealthbox's UI). Strip to
    // plaintext for the textarea so users edit prose, not raw markup.
    description: htmlToPlainText(task?.description || ''),
    // Wealthbox returns the priority STRING ('None' | 'Low' | 'Medium' | 'High')
    // and the category as `category` (singular int), NOT `category_id`. Map
    // server `'None'` to the empty-string sentinel the dropdown uses so the
    // "(None)" option shows as selected; the submit handler maps back to null.
    priority: (task?.priority && task.priority !== 'None') ? task.priority : '',
    category: task?.category ?? ''
    // Assignee editing is intentionally not exposed: would require a Wealthbox
    // /v1/users picker. When added, use `assigned_to` (not `assignee_id`).
  }
}

function ViewingBody ({ task, categoryOptions }) {
  const dueFormatted = task.due_date ? dayjs(task.due_date).format('ddd, MMM D, YYYY') : null
  const startFormatted = task.start_date ? dayjs(task.start_date).format('ddd, MMM D, YYYY') : null

  // Show all linked contacts (Wealthbox allows multi-link, e.g. a meeting/task
  // tied to multiple household members). The row preview shows only the first
  // name + a "+N" hint; here we render the full comma-separated list.
  const linkedNames = Array.isArray(task.linked_to)
    ? task.linked_to.map(l => l?.name).filter(Boolean)
    : []
  const linkedLabel = linkedNames.length ? linkedNames.join(', ') : null
  // Wealthbox response uses `category` (number id), NOT `category_id` as the
  // original SMT-1352 plan stated. Look the id up in our pre-fetched options
  // list (loaded by the parent modal when in calendar form mode) to render
  // the human-readable label; if categories haven't been loaded (simple mode
  // or fetch failed) we fall back to "Category #<id>" so we don't pretend
  // the task is uncategorized.
  const categoryLabel = useMemo(() => {
    if (task.category == null) return null
    const cat = categoryOptions.find(c => c.value === task.category)
    return cat?.label ?? `Category #${task.category}`
  }, [task.category, categoryOptions])

  // Wealthbox priority comes back as a human-readable string already
  // ('None' | 'Low' | 'Medium' | 'High'). 'None' is a valid sentinel meaning
  // "explicitly no priority"; we hide it the same as null/undefined since
  // showing "Priority: None" reads as noise next to actual values.
  const priorityLabel = task.priority && task.priority !== 'None' ? task.priority : null

  const isCompleted = !!task.complete

  return (
    <Grid container spacing={2}>
      {linkedLabel ? (
        <Grid item xs={12} sm={(startFormatted || dueFormatted) ? 6 : 12}>
          <div style={labelStyle}>{linkedNames.length > 1 ? 'Linked Contacts' : 'Linked Contact'}</div>
          <div style={valueStyle}>{linkedLabel}</div>
        </Grid>
      ) : null}
      {(startFormatted || dueFormatted) ? (
        <Grid item xs={12} sm={linkedLabel ? 6 : 12}>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {startFormatted ? (
              <div>
                <div style={labelStyle}>Start Date</div>
                <div style={valueStyle}>{startFormatted}</div>
              </div>
            ) : null}
            {dueFormatted ? (
              <div>
                <div style={labelStyle}>Due Date</div>
                <div style={valueStyle}>{dueFormatted}</div>
              </div>
            ) : null}
          </div>
        </Grid>
      ) : null}

      {priorityLabel ? (
        <Grid item xs={12} sm={categoryLabel ? 4 : 12}>
          <div style={labelStyle}>Priority</div>
          <div style={valueStyle}>{priorityLabel}</div>
        </Grid>
      ) : null}
      {categoryLabel ? (
        <Grid item xs={12} sm={priorityLabel ? 8 : 12}>
          <div style={labelStyle}>Category</div>
          <div style={valueStyle}>{categoryLabel}</div>
        </Grid>
      ) : null}

      {/* Assignee editing/display is intentionally out of scope: Wealthbox
          returns `assigned_to` as a bare user id with no embedded name and we
          don't have a user-directory lookup hook today. When product asks for
          it, fetch via /v1/users and look up names by id. */}

      <Grid item xs={12}>
        <div style={labelStyle}>Description</div>
        {task.description ? (
          <div style={valueStyle}>
            <Markup value={task.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        ) : (
          <div style={{ ...valueStyle, color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>
            No description
          </div>
        )}
      </Grid>

      {isCompleted ? (
        <Grid item xs={12}>
          <div className='font-size-sm' style={{ color: 'var(--color-growth-500)', fontWeight: 600 }}>
            This task has been completed
          </div>
        </Grid>
      ) : null}
    </Grid>
  )
}

ViewingBody.propTypes = {
  task: PropTypes.object.isRequired,
  categoryOptions: PropTypes.array
}

function EditingBody ({ task, formMode, categoryOptions, onSubmit, onCancel }) {
  const isCalendar = formMode === 'calendar'
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const { control, handleSubmit } = useForm({
    defaultValues: buildFormDefaults(task)
  })

  const submit = handleSubmit(async (values) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Build a partial body with only the fields we want to send. Wealthbox
      // PUT /v1/tasks/{id} accepts partial updates; sending null clears the
      // value, so the "(None)" option (value === '') becomes priority: null /
      // category: null. Field names match the Wealthbox response/write shape:
      // `priority` (string), `category` (number), `assigned_to` (number) - not
      // `category_id` / `assignee_id` as the original SMT-1352 plan incorrectly
      // stated.
      const body = {
        name: values.name?.trim() || undefined,
        due_date: values.due_date || undefined,
        description: values.description ?? undefined,
        priority: values.priority === '' ? null : values.priority,
        category: values.category === '' ? null : values.category
      }
      await onSubmit(body)
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to save task'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <form onSubmit={submit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={isCalendar ? 8 : 9}>
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <SydLabel label='Task Name' required>
                <SydInput {...field} value={field.value || ''} placeholder='Task name' />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={isCalendar ? 4 : 3}>
          <Controller
            name='due_date'
            control={control}
            render={({ field }) => (
              <SydLabel label='Due Date'>
                <SydDatePicker
                  type='date'
                  dateFormat='YYYY-MM-DD'
                  value={field.value || null}
                  onChange={(d) => field.onChange(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                />
              </SydLabel>
            )}
          />
        </Grid>

        {isCalendar ? (
          <>
            <Grid item xs={12} sm={4}>
              <Controller
                name='priority'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Priority'>
                    <SydSelect
                      options={PRIORITY_OPTIONS}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller
                name='category'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Category'>
                    <SydSelect
                      options={[
                        { label: '(None)', value: '' },
                        ...categoryOptions
                      ]}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value
                        field.onChange(raw === '' ? '' : Number(raw))
                      }}
                    />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Description'>
                    <SydTextArea
                      {...field}
                      value={field.value || ''}
                      rows={4}
                      placeholder='Description'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          </>
        ) : null}
      </Grid>

      {submitError ? (
        <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>
          {submitError}
        </div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={submitting} type='button'>
          Cancel
        </SydButton>
        <SydButton variant='primary' size='sm' processing={submitting} type='submit'>
          Save Changes
        </SydButton>
      </SydModalActions>
    </form>
  )
}

EditingBody.propTypes = {
  task: PropTypes.object.isRequired,
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  categoryOptions: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

function WealthboxTaskDetailModal ({
  open,
  onClose,
  task,
  enableEdit = false,
  formMode = 'simple',
  invalidateQueryKeys
}) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)
  const [categoryOptions, setCategoryOptions] = useState([])

  useEffect(() => {
    if (open) setMode(MODE_VIEWING)
  }, [open, task?.id])

  // Lazy-load categories the first time the modal opens (only relevant for
  // edit mode + calendar form mode, but cheap enough to fetch once and reuse).
  useEffect(() => {
    if (!open || !enableEdit || formMode !== 'calendar') return
    let cancelled = false
    fetchTaskCategories({ clientId })
      .then(opts => { if (!cancelled) setCategoryOptions(opts) })
      .catch(() => { /* category fetch is non-critical; leave list empty */ })
    return () => { cancelled = true }
  }, [open, enableEdit, formMode, clientId])

  const invalidateAll = useCallback(() => {
    if (!invalidateQueryKeys) return
    for (const key of invalidateQueryKeys) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }, [invalidateQueryKeys, queryClient])

  const { mutateAsync: handleEditSubmit } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'modifyTask',
      clientId,
      request: { taskId: task.id, body }
    }),
    onSuccess: () => {
      onClose()
      invalidateAll()
    }
  })

  const { mutate: handleComplete, isLoading: completing, error: completeError } = useMutation({
    mutationFn: () => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'completeTask',
      clientId,
      request: { taskId: task.id }
    }),
    onSuccess: () => {
      onClose()
      invalidateAll()
    }
  })

  const { mutate: handleDelete, isLoading: deleting, error: deleteError } = useMutation({
    mutationFn: () => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'deleteTask',
      clientId,
      request: { taskId: task.id }
    }),
    onSuccess: () => {
      onClose()
      invalidateAll()
    }
  })

  if (!task) return null

  const isCompleted = !!task.complete
  const titles = {
    [MODE_VIEWING]: task.name || 'Task Details',
    [MODE_EDITING]: 'Edit Task',
    [MODE_CONFIRMING_DELETE]: 'Delete Task?'
  }

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title={titles[mode]}
      size='xmedium'
      disablePortal={false}
    >
      {mode === MODE_VIEWING && (
        <>
          <ViewingBody task={task} categoryOptions={categoryOptions} />
          {completeError ? (
            <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)', marginTop: '8px' }}>
              {completeError?.message || 'Failed to complete task'}
            </div>
          ) : null}
          <SydModalActions>
            {/* Destructive action on the far left so it sits visually
                separated from the safe / primary actions. Only render the
                Delete button when editing is allowed - read-only mode never
                exposes destructive actions. */}
            {enableEdit ? (
              <SydButton variant='ghost' size='sm' icon='delete' onClick={() => setMode(MODE_CONFIRMING_DELETE)}>
                Delete
              </SydButton>
            ) : null}
            <div style={{ flex: 1 }} />
            <SydButton variant='ghost' size='sm' onClick={onClose}>
              Close
            </SydButton>
            {enableEdit ? (
              <SydButton variant='outline' size='sm' icon='edit' onClick={() => setMode(MODE_EDITING)}>
                Edit
              </SydButton>
            ) : null}
            {enableEdit && !isCompleted ? (
              <SydButton variant='primary' size='sm' processing={completing} onClick={() => handleComplete()}>
                Complete
              </SydButton>
            ) : null}
          </SydModalActions>
        </>
      )}

      {mode === MODE_EDITING && (
        <EditingBody
          task={task}
          formMode={formMode}
          categoryOptions={categoryOptions}
          onSubmit={handleEditSubmit}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_CONFIRMING_DELETE && (
        <>
          <div className='font-size-sm' style={{ padding: '4px 0 8px' }}>
            Delete <strong>{task.name || 'this task'}</strong>? This cannot be undone — the task
            will be removed from Wealthbox entirely.
          </div>
          {deleteError ? (
            <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>
              {deleteError?.message || 'Failed to delete task'}
            </div>
          ) : null}
          <SydModalActions>
            <SydButton variant='ghost' size='sm' onClick={() => setMode(MODE_VIEWING)} disabled={deleting}>
              Cancel
            </SydButton>
            <SydButton variant='primary' size='sm' processing={deleting} onClick={() => handleDelete()}>
              Delete
            </SydButton>
          </SydModalActions>
        </>
      )}
    </SydModal>
  )
}

WealthboxTaskDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  task: PropTypes.object,
  enableEdit: PropTypes.bool,
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default WealthboxTaskDetailModal
